import { FC } from "react";
import { graphql, useFragment } from "react-relay";
import { useNavigate } from "react-router-dom";
import {
  Decimal,
  IconDenom,
  classApr,
  formatApr,
  useLocale,
  useTranslation,
} from "rujira.ui";
import { navigateOnModifiedClick } from "../../common/navigateOnModifiedClick";
import { siteOrigin } from "../../services/siteOrigin";
import { Subscription } from "../../services/useNodeSubscription";
import { StakingShareButton } from "../../sharing/StrategyCard.tsx";
import { stakeRoute } from "../../staking/routes";
import { StakingPoolPortfolioRowFragment$key } from "./__generated__/StakingPoolPortfolioRowFragment.graphql";
import { PortfolioManageButton } from "./PortfolioManageButton";

const row = graphql`
  fragment StakingPoolPortfolioRowFragment on StakingAccount {
    id
    pool {
      bondAsset {
        metadata {
          symbol
        }
      }
      status {
        valueUsd
      }
      summary {
        apr {
          value
          status
        }
      }
    }
    bonded {
      amount
    }
    liquidSize {
      amount
    }
    pendingRevenue {
      amount
      asset {
        metadata {
          symbol
        }
      }
    }
    valueUsd
  }
`;

const subscription = graphql`
  subscription StakingPoolPortfolioRowSubscription($id: ID!) {
    node(id: $id) {
      ... on StakingAccount {
        valueUsd
        pool {
          status {
            valueUsd
          }
          summary {
            apr {
              value
              status
            }
          }
        }
      }
    }
  }
`;

export const StakingPoolPortfolioRow: FC<{
  k: StakingPoolPortfolioRowFragment$key;
}> = ({ k }) => {
  const { t } = useTranslation("strategies");
  const { toRoot } = useLocale();
  const { pool, valueUsd, id, bonded, liquidSize, pendingRevenue } =
    useFragment(row, k);
  const assetLabel = pool.bondAsset.metadata.symbol || "";
  const url = toRoot(stakeRoute(pool.bondAsset));
  const shareUrl = siteOrigin() + url;
  const navigate = useNavigate();
  return (
    <tr
      onClick={(event) => navigateOnModifiedClick(event, navigate, url)}
      className="pointer">
      <Subscription id={id} subscription={subscription} />
      <td>
        <IconDenom denom={assetLabel} />
      </td>
      <td className="fs-16 fw-500" style={{ paddingLeft: "0" }}>
        <span>{assetLabel}</span>
      </td>
      <td>
        <div className="tag tag--primary ml-a mr-0">{t("staking")}</div>
      </td>
      <td>
        <Decimal
          symbol={assetLabel}
          amount={BigInt(bonded.amount) + BigInt(liquidSize.amount)}
          round={4}
          clip
          symbolClassName="decimal__symbol--small color-grey condensed fw-500 mr-1"
        />
        {BigInt(pendingRevenue.amount) ? (
          <Decimal
            symbol={pendingRevenue.asset.metadata.symbol}
            amount={BigInt(pendingRevenue.amount)}
            round={4}
            clip
            symbolClassName="decimal__symbol--small color-grey condensed fw-500 mr-1"
          />
        ) : null}
      </td>
      <td className={`text-center ${classApr(pool.summary.apr)}`}>
        {formatApr(pool.summary.apr)}
      </td>
      <td className="text-right portfolio__share-action">
        <Decimal
          amount={BigInt(valueUsd || 0)}
          symbol="$"
          symbolLeft
          round={2}
          clip
        />
      </td>
      <td className="text-right">
        <StakingShareButton
          copyUrl={shareUrl}
          assetLabel={assetLabel}
          apr={pool.summary.apr}
          tvl={pool.status?.valueUsd || 0n}
        />
      </td>
      <PortfolioManageButton to={url} />
    </tr>
  );
};
